// ---------------------------------------------------------------------------
// The open rooms, and one room opened up.
//
// Split out of `AdminScreen.tsx`. The two are together because they are one
// drill-down: the list is the summary the server polls, and the detail is what
// arrives when a code is clicked. The detail is also the only place in the app
// that renders drafts, IPs and device ids, which is why the whole subtree is
// mounted behind the admin-mode gate rather than hidden with a class.
// ---------------------------------------------------------------------------

import type { AdminRoomDetail, AdminRoomSummary } from '@countdown/shared';
import { Button, Card, CardTitle } from '../../components/primitives/index.js';
import { ConfirmAction, clockTime } from './atoms.js';
import type { BanDraft } from './atoms.js';

export function Rooms({
  rooms,
  onOpen,
  onClose,
}: {
  rooms: AdminRoomSummary[];
  onOpen: (code: string) => void;
  onClose: (code: string) => void;
}) {
  return (
    <Card>
      <CardTitle>Rooms</CardTitle>
      {rooms.length === 0 ? (
        <p className="mt-3 text-sm text-ink-dim">No rooms open.</p>
      ) : (
        <ul className="mt-3 flex flex-col divide-y divide-line">
          {rooms.map((room) => (
            <li key={room.roomCode} className="flex flex-wrap items-center gap-3 py-3">
              <button
                type="button"
                onClick={() => onOpen(room.roomCode)}
                className="min-h-11 font-mono text-lg font-semibold tracking-[0.1em] text-blue-200"
              >
                {room.roomCode}
              </button>
              <p className="text-sm text-ink-muted">
                {room.phase} · {room.playerCount} playing, {room.spectatorCount} watching,{' '}
                {room.botCount} bots · {room.visibility} · host {room.hostName}
              </p>
              <p className="font-mono text-xs text-ink-dim">last seen {clockTime(room.lastActivityAt)}</p>
              <div className="ml-auto">
                <ConfirmAction
                  label="Close"
                  question={`Close ${room.roomCode} and send its players home?`}
                  confirmLabel={`Yes, close ${room.roomCode}`}
                  onConfirm={() => onClose(room.roomCode)}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function RoomDetail({
  detail,
  onDismiss,
  onBanSubject,
  onForceRename,
}: {
  detail: AdminRoomDetail;
  onDismiss: () => void;
  onBanSubject: (draft: BanDraft) => void;
  onForceRename: (playerId: string) => void;
}) {
  const { summary, state, drafts, connections } = detail;

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <CardTitle>Room {summary.roomCode}</CardTitle>
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Close panel
        </Button>
      </div>
      <p className="mt-2 text-sm text-ink-muted">
        {state.phase} · round {state.currentRoundIndex + 1} of {state.totalRounds}
      </p>

      <ul className="mt-3 flex flex-col divide-y divide-line">
        {state.players.map((player) => {
          const connection = connections.find((entry) => entry.playerId === player.id);
          const draft = drafts[player.id];
          return (
            <li key={player.id} className="flex flex-col gap-1.5 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{player.displayName}</span>
                <span className="rounded-chip border border-line bg-surface px-2 py-0.5 font-mono text-xs text-ink-muted">
                  {/* The model id rather than `medium`, which is what every AI
                      seat carries in `botDifficulty` and which tells an admin
                      nothing about the seat they are looking at. */}
                  {player.isBot
                    ? player.botKind === 'llm'
                      ? `ai ${player.botModel ?? ''}`.trim()
                      : `bot ${player.botDifficulty ?? ''}`.trim()
                    : (player.username ?? 'guest')}
                </span>
                <span className="font-mono text-xs text-ink-dim">
                  {player.role} · {player.connected ? 'connected' : 'dropped'} · {player.score}
                </span>
                {!player.isBot && (
                  <div className="ml-auto">
                    <ConfirmAction
                      label="Force rename"
                      question={`Reset ${player.displayName}'s display name?`}
                      confirmLabel="Yes, rename"
                      onConfirm={() => onForceRename(player.id)}
                    />
                  </div>
                )}
              </div>
              {draft && (
                <p className="font-mono text-xs text-ink-dim">
                  draft: {draft.kind === 'letters' && draft.word}
                  {draft.kind === 'conundrum' && draft.answer}
                  {draft.kind === 'numbers' &&
                    draft.steps.map((step) => `${step.id}=${step.result}`).join(' ')}
                </p>
              )}
              {connection && (
                <p className="flex flex-wrap items-center gap-2 font-mono text-xs text-ink-dim">
                  <span>rtt {connection.rttMs ?? '?'}ms</span>
                  <span>seen {clockTime(connection.lastHeartbeat)}</span>
                  {/* IP, device and user id are here because a ban needs a
                      subject, and this is the only place the server exposes one. */}
                  {connection.ip && (
                    <button
                      type="button"
                      className="min-h-11 underline"
                      onClick={() =>
                        onBanSubject({
                          subjectKind: 'ip',
                          subject: connection.ip ?? '',
                          scope: 'global',
                          roomCode: '',
                          reason: '',
                        })
                      }
                    >
                      ban ip {connection.ip}
                    </button>
                  )}
                  {connection.deviceId && (
                    <button
                      type="button"
                      className="min-h-11 underline"
                      onClick={() =>
                        onBanSubject({
                          subjectKind: 'device',
                          subject: connection.deviceId ?? '',
                          scope: 'global',
                          roomCode: '',
                          reason: '',
                        })
                      }
                    >
                      ban device
                    </button>
                  )}
                  {connection.userId && (
                    <button
                      type="button"
                      className="min-h-11 underline"
                      onClick={() =>
                        onBanSubject({
                          subjectKind: 'user',
                          subject: connection.userId ?? '',
                          scope: 'global',
                          roomCode: '',
                          reason: '',
                        })
                      }
                    >
                      ban account
                    </button>
                  )}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
